/**
 * This class offers a Promise-like interface but replaces `then` with `subscribe`.
 * The function passed into subscribe will then be called each time new data is available.
 * The onResult fn will also be given a `final` param in case the calling code needs to know
 * if the final chunk of data has been received.
 */
export class Stream {
    subscribed = false;
    onResult;
    onError;
    onEnd;
    constructor(initialiser) {
        initialiser((val, final) => {
            if (this.onResult) {
                this.onResult(val, final);
            }
            if (final && this.onEnd) {
                this.onEnd();
            }
        }, (reason) => {
            if (this.onError) {
                this.onError(reason);
            }
        });
    }
    subscribe(subscription) {
        if (this.subscribed) {
            throw new Error("Already subscribed");
        }
        this.subscribed = true;
        this.onResult = subscription.onResult;
        this.onError = subscription.onError;
        this.onEnd = subscription.onEnd;
    }
    map(mapFn) {
        return new Stream((resolve, reject) => {
            let index = 0;
            this.subscribe({
                onResult: (val, final) => resolve(mapFn(val, index++), final),
                onError: reject,
            });
        });
    }
    mapAsync(mapFn) {
        return new Stream((resolve, reject) => {
            let index = 0;
            this.subscribe({
                onResult: (val, final) => mapFn(val, index++)
                    .then((mapped) => resolve(mapped, final))
                    .catch(reject),
                onError: reject,
            });
        });
    }
    aggregate(callbackFn, initialValue) {
        return new Stream((resolve, reject) => {
            let agg = initialValue;
            let index = 0;
            this.subscribe({
                onResult: (val, final) => {
                    try {
                        agg = callbackFn(agg, val, index++);
                    }
                    catch (err) {
                        reject(err);
                        throw err;
                    }
                    resolve(agg, final);
                },
                onError: reject,
            });
        });
    }
    toPromise(aggFn) {
        return new Promise((resolve, reject) => {
            let agg;
            this.subscribe({
                onResult: (val, final) => {
                    agg = agg === undefined || aggFn === undefined ? val : aggFn(agg, val);
                    if (final) {
                        resolve(agg);
                    }
                },
                onError: reject,
            });
        });
    }
}
